import { StrictMode, useEffect } from "react";
import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, useLocation } from "react-router-dom";
import { initMetaPixel } from "@/lib/meta-pixel";
import { startEngagementTracking } from "@/lib/engaged-visit";
import { apiUrl } from "@/lib/api";
import { AppErrorBoundary } from "@/components/error-boundary";
import { RevealObserver } from "@/components/reveal-observer";
import { AppRoutes, preloadRouteFor } from "@/app";
import "@/index.css";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: true,
      retry: 1,
      staleTime: 30_000,
    },
  },
});

// Loads the base pixel code and fires the first PageView. No-op when
// VITE_META_PIXEL_ID is unset (local/staging), so this is safe to call
// unconditionally — see apps/web/src/lib/meta-pixel.ts.
initMetaPixel();

/**
 * Meta CAPI "EngagedVisit": 30s active + 50% scroll on a public marketing page.
 * Resets per route (the SPA never reloads). See apps/web/src/lib/engaged-visit.ts.
 */
let navigateEngagement: ((pathname: string) => void) | null = null;
function EngagedVisitTracker() {
  const location = useLocation();
  useEffect(() => {
    navigateEngagement ??= startEngagementTracking(apiUrl);
    navigateEngagement(location.pathname);
  }, [location.pathname]);
  return null;
}

// createRoot, not hydrateRoot, even though public pages arrive with their
// markup already in #root (scripts/prerender.mjs). That markup is for crawlers
// and for first paint; React replaces it with an identical tree in one commit.
// Hydrating instead would make every query-string-dependent branch (signup
// plans, the lifetime countdown, a signed-in header) a hydration mismatch.
function start() {
  createRoot(document.getElementById("root")!).render(
    <StrictMode>
      {/* Outside BrowserRouter on purpose: a crash inside the router would take
          react-router's Link down with it, so the fallback screen uses plain
          anchors and a reload instead. */}
      <AppErrorBoundary>
        <QueryClientProvider client={queryClient}>
          <RevealObserver />
          <BrowserRouter>
            <EngagedVisitTracker />
            <AppRoutes />
          </BrowserRouter>
        </QueryClientProvider>
      </AppErrorBoundary>
    </StrictMode>,
  );
}

// Each public page is its own chunk (src/lib/lazy-route.ts). The one this URL
// needs is fetched before the first render, so that commit replaces the
// prerendered page with the page itself, not with a loading card. If the
// fetch fails, render anyway: the page falls back to its lazy path and the
// error boundary reports whatever is wrong.
preloadRouteFor(window.location.pathname).then(start, start);
